import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Geometry } from 'geojson';
import { of, Observable } from 'rxjs';
import { map, tap, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CrudService } from 'src/app/core/service/crud-service';
import { Pageable } from 'src/app/core/service/pageable';
import { StoreGradeRecord, StoreGradeResponse } from './store-grade.model';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

/** A stored grade read is a table lookup, so it should be quick. */
const GRADE_READ_TIMEOUT_MS = 20000;

/** A refresh recalculates the grade live and takes several seconds. */
const GRADE_REFRESH_TIMEOUT_MS = 90000;

@Injectable({
  providedIn: 'root'
})
export class AssetReviewService extends CrudService<any> {
  protected path = '/asset-review';

  private storeDetailsCache = new Map<string, CacheEntry<any>>();
  private realEstateQualityCache = new Map<string, CacheEntry<any>>();
  private gradeBreakdownChartCache = new Map<string, CacheEntry<any>>();
  private bannerStrengthCache = new Map<string, CacheEntry<any>>();
  private storePerformanceCache = new Map<string, CacheEntry<any>>();
  private competitiveImpactCache = new Map<string, CacheEntry<any>>();
  private dataValidationCache = new Map<string, CacheEntry<any>>();
  private finalGradeCache = new Map<string, CacheEntry<any>>();
  private storeGradeCache = new Map<string, CacheEntry<StoreGradeResponse>>();

  // Cache persists until page refresh or logout - no expiry timer

  constructor(protected http: HttpClient) {
    super(http);
  }

  protected buildInstance(raw: any): any {
    return raw;
  }

  protected buildPageableInstance(raw: any): Pageable<any> {
    return {
      ...raw,
      content: raw?.content?.map((item: any) => this.buildInstance(item)) || []
    };
  }

  assetReviewStoreDetails(uuid: string, forceRefresh: boolean = false): Observable<any> {
    if (!forceRefresh && this.isCacheValid(this.storeDetailsCache, uuid)) {
      const cached = this.getCachedData(this.storeDetailsCache, uuid);
      return of(cached.data);
    }

    const url = `store-details/${uuid}`;
    return super.findOne(url).pipe(
      tap((result: any) => {
        this.setCachedData(this.storeDetailsCache, uuid, result);
      })
    );
  }

  assetReviewRealEstateQuality(uuid: string, forceRefresh: boolean = false): Observable<any> {
    if (!forceRefresh && this.isCacheValid(this.realEstateQualityCache, uuid)) {
      const cached = this.getCachedData(this.realEstateQualityCache, uuid);
      return of(cached.data);
    }

    const url = `real-estate-quality/${uuid}`;
    return super.findOne(url).pipe(
      tap((result: any) => {
        this.setCachedData(this.realEstateQualityCache, uuid, result);
      })
    );
  }

  assetReviewGradeBreakdownChart(uuid: string, forceRefresh: boolean = false): Observable<any> {
    if (!forceRefresh && this.isCacheValid(this.gradeBreakdownChartCache, uuid)) {
      const cached = this.getCachedData(this.gradeBreakdownChartCache, uuid);
      return of(cached.data);
    }

    const url = `overall-grades-breakdown`;
    return super.findOne(url).pipe(
      tap((result: any) => {
        this.setCachedData(this.gradeBreakdownChartCache, uuid, result);
      })
    );
  }

  assetReviewBannerStrength(
    storeUuid: string,
    bannerUuid: string,
    point: { type: string; coordinates: number[] },
    geometry: Geometry,
    forceRefresh: boolean = false
  ): Observable<any> {
    const cacheKey = `${storeUuid}-${bannerUuid}-${JSON.stringify(point)}`;

    if (!forceRefresh && this.isCacheValid(this.bannerStrengthCache, cacheKey)) {
      const cached = this.getCachedData(this.bannerStrengthCache, cacheKey);
      return of(cached.data);
    }

    const url = `${this.buildUrl()}/banner-strength/${storeUuid}`;
    const body = { bannerUuid, point, geometry };

    return this.http.post<any>(url, body).pipe(
      tap((result: any) => {
        this.setCachedData(this.bannerStrengthCache, cacheKey, result);
      })
    );
  }

  assetReviewDataValidation(
    uuid: string,
    requestData: {
      point: { lat: number; lng: number };
      travelTimeMinutes: number;
      locationUuid?: string | null;
      fips: string;
    },
    forceRefresh: boolean = false
  ): Observable<any> {
    const cacheKey = `${uuid}-${JSON.stringify(requestData)}`;

    if (!forceRefresh && this.isCacheValid(this.dataValidationCache, cacheKey)) {
      const cached = this.getCachedData(this.dataValidationCache, cacheKey);
      return of(cached.data);
    }

    const url = `${this.buildUrl()}/SalesVolume/Coverage/${uuid}`;
    const body = {
      point: requestData.point,
      travelTimeMinutes: requestData.travelTimeMinutes,
      locationUuid: requestData.locationUuid,
      fips: requestData.fips
    };

    return this.http.post<any>(url, body).pipe(
      tap((result: any) => {
        this.setCachedData(this.dataValidationCache, cacheKey, result);
      })
    );
  }

  assetReviewStorePerformance(
    uuid: string,
    requestData: {
      point: { lat: number; lng: number };
      travelTimeMinutes: number;
      locationUuid?: string | null;
      comparisonType: string;
      bannerUuid: string;
      fips: string;
      fit:string;
    },
    forceRefresh: boolean = false
  ): Observable<any> {
    const cacheKey = `${uuid}-${JSON.stringify(requestData)}`;

    if (!forceRefresh && this.isCacheValid(this.storePerformanceCache, cacheKey)) {
      const cached = this.getCachedData(this.storePerformanceCache, cacheKey);
      return of(cached.data);
    }

    const url = `${this.buildUrl()}/store-performance/${uuid}`;
    const body = {
      point: requestData.point,
      travelTimeMinutes: requestData.travelTimeMinutes,
      locationUuid: requestData.locationUuid,
      comparisonType: requestData.comparisonType,
      bannerUuid: requestData.bannerUuid,
      fips: requestData.fips,
      fit:requestData.fit
    };

    return this.http.post<any>(url, body).pipe(
      tap((result: any) => {
        this.setCachedData(this.storePerformanceCache, cacheKey, result);
      })
    );
  }

  getCompetitiveImpact(storeUuid: string, requestBody: any, forceRefresh: boolean = false): Observable<any> {
    const cacheKey = `${storeUuid}-${JSON.stringify(requestBody)}`;

    if (!forceRefresh && this.isCacheValid(this.competitiveImpactCache, cacheKey)) {
      const cached = this.getCachedData(this.competitiveImpactCache, cacheKey);
      return of(cached.data);
    }

    const url = `${this.buildUrl()}/competitive-impact/${storeUuid}`;

    return this.http.post<any>(url, requestBody).pipe(
      tap((result: any) => {
        this.setCachedData(this.competitiveImpactCache, cacheKey, result);
      })
    );
  }

  getFinalGrade(payload: any, forceRefresh: boolean = false): Observable<{ overAllGrade: string }> {
    const cacheKey = JSON.stringify(payload);

    if (!forceRefresh && this.isCacheValid(this.finalGradeCache, cacheKey)) {
      const cached = this.getCachedData(this.finalGradeCache, cacheKey);
      return of(cached.data);
    }

    const url = `${this.buildUrl()}/final-grade`;
    return this.http.post<{ overAllGrade: string }>(url, payload).pipe(
      tap((result: any) => {
        this.setCachedData(this.finalGradeCache, cacheKey, result);
      })
    );
  }

  /**
   * The two grade endpoints go straight to the AWS gateway rather than through mtn-insights.
   *
   * Same approach the P&C predict API already uses (see StoreAddService): the key ships in the
   * environment file and the browser calls the gateway directly. It lets Asset Review reach prod
   * without waiting on a backend deployment.
   *
   * Two consequences worth knowing. The key is readable in the bundle, as PREDICT_API_KEY
   * already is. And the license check the proxy applied -- @PreAuthorize("hasLicense('STANDARD')")
   * -- is gone, so nothing server side now restricts who can read a grade.
   */
  private gradeUrl(uuid: string, refresh: boolean): string {
    const base = environment.GRADE_API_BASE_URL.replace(/\/+$/, '');
    return `${base}/stores/${encodeURIComponent(uuid)}/grade${refresh ? '/refresh' : ''}`;
  }

  /**
   * Both endpoints are GET upstream. The proxy exposed refresh as a POST because that read
   * better as an action, but the gateway itself only answers GET.
   */
  private gradeHeaders(): HttpHeaders {
    return new HttpHeaders({ 'x-api-key': environment.GRADE_API_KEY });
  }

  /** The gateway omits storeUuid on some paths; keep the response self describing. */
  private withStoreUuid(response: StoreGradeResponse, uuid: string): StoreGradeResponse {
    return response && !response.storeUuid ? { ...response, storeUuid: uuid } : response;
  }

  /**
   * Fills in hasDetail, which the gateway does not send.
   *
   * The card and the breakdown adapter both branch on it to decide whether five subscores can be
   * rendered or only the overall grade. Undefined reads as false, so without this every record
   * looked like one written before the detail columns existed and the subscores never appeared.
   *
   * Derived from the record: a record has detail when all five component blocks are present. An
   * explicit value from the API wins, so this stops mattering if the gateway starts sending one.
   */
  private withDetailFlag(record: StoreGradeRecord | null): StoreGradeRecord | null {
    if (!record) {
      return record;
    }
    if (typeof record.hasDetail === 'boolean') {
      return record;
    }
    const hasDetail = !!(
      record.totalSalesPerformance &&
      record.salesPerSqft &&
      record.realEstateQuality &&
      record.bannerStrength &&
      record.competitiveImpact
    );
    return { ...record, hasDetail };
  }

  /** Applies the record level fixups to the current record and every history row. */
  private normaliseGradeResponse(response: StoreGradeResponse, uuid: string): StoreGradeResponse {
    const withUuid = this.withStoreUuid(response, uuid);
    if (!withUuid) {
      return withUuid;
    }
    return {
      ...withUuid,
      current: this.withDetailFlag(withUuid.current),
      history: (withUuid.history || []).map(record => this.withDetailFlag(record))
    };
  }

  /**
   * Stored grade for a store, with its history.
   *
   * Cached per store so opening the spotlight card, the full breakdown panel and the detail
   * page all read the same record. That is what keeps the grade identical across those screens
   * and lets the breakdown open without another round trip.
   */
  getStoreGrade(uuid: string, forceRefresh: boolean = false): Observable<StoreGradeResponse> {
    if (!forceRefresh && this.isCacheValid(this.storeGradeCache, uuid)) {
      return of(this.getCachedData(this.storeGradeCache, uuid).data);
    }

    return this.http
      .get<StoreGradeResponse>(this.gradeUrl(uuid, false), { headers: this.gradeHeaders() })
      .pipe(
        timeout(GRADE_READ_TIMEOUT_MS),
        map((result: StoreGradeResponse) => this.normaliseGradeResponse(result, uuid)),
        tap((result: StoreGradeResponse) => {
          this.setCachedData(this.storeGradeCache, uuid, result);
        })
      );
  }

  /**
   * Recalculates the grade live for the Generate Fresh Grade action.
   *
   * Never cached, because the point is to get today's numbers. The result is not stored upstream
   * either, so it is shown as an unsaved preview and its record has no id.
   */
  refreshStoreGrade(uuid: string): Observable<StoreGradeResponse> {
    return this.http
      .get<StoreGradeResponse>(this.gradeUrl(uuid, true), { headers: this.gradeHeaders() })
      .pipe(
        timeout(GRADE_REFRESH_TIMEOUT_MS),
        map((result: StoreGradeResponse) => this.normaliseGradeResponse(result, uuid))
      );
  }

  private isCacheValid<T>(cache: Map<string, CacheEntry<T>>, key: string): boolean {
    return cache.has(key);
  }

  private getCachedData<T>(cache: Map<string, CacheEntry<T>>, key: string): CacheEntry<T> | null {
    return cache.get(key) || null;
  }

  private setCachedData<T>(cache: Map<string, CacheEntry<T>>, key: string, data: T): void {
    cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  clearCacheForStore(uuid: string): void {
    this.storeDetailsCache.delete(uuid);
    this.realEstateQualityCache.delete(uuid);
    this.gradeBreakdownChartCache.delete(uuid);
    this.storeGradeCache.delete(uuid);

    const clearByPattern = (cache: Map<string, any>) => {
      Array.from(cache.keys())
        .filter(key => key.startsWith(uuid))
        .forEach(key => cache.delete(key));
    };

    clearByPattern(this.bannerStrengthCache);
    clearByPattern(this.storePerformanceCache);
    clearByPattern(this.competitiveImpactCache);
    clearByPattern(this.dataValidationCache);
  }

  isStoreDetailsCached(uuid: string): boolean {
    return this.isCacheValid(this.storeDetailsCache, uuid);
  }

  clearAllCache(): void {
    this.storeDetailsCache.clear();
    this.realEstateQualityCache.clear();
    this.gradeBreakdownChartCache.clear();
    this.bannerStrengthCache.clear();
    this.storePerformanceCache.clear();
    this.competitiveImpactCache.clear();
    this.dataValidationCache.clear();
    this.finalGradeCache.clear();
    this.storeGradeCache.clear();
  }

  logCacheStats(): void {
    const stats = {
      storeDetails: this.storeDetailsCache.size,
      realEstateQuality: this.realEstateQualityCache.size,
      gradeBreakdown: this.gradeBreakdownChartCache.size,
      bannerStrength: this.bannerStrengthCache.size,
      storePerformance: this.storePerformanceCache.size,
      competitiveImpact: this.competitiveImpactCache.size,
      dataValidation: this.dataValidationCache.size,
      finalGrade: this.finalGradeCache.size
    };
    console.table(stats);
  }


}
